// ── 모바일 매장 카드 + 상세 섹션 + 연결 해제 버튼 ────────────────────────────
// StoreCard: 접힘/펼침, 드래그·탭 연결, 계약/택배/홍보/카드/전화/입금 상세
// DetailSection: 섹션 헤더 + 합계 + 자식 행 렌더링
// UnlinkBtn: × 버튼으로 개별 결제 연결 해제

import React from 'react';
import { C } from './constants';
import { fmt, fmtDate } from './utils';
import { s } from './mobileStyles';
import { CopyTrackingButton } from './CopyTrackingButton';
import { DetailSection } from './StoreCardDetail';
import { StoreCardIncomeDetails } from './StoreCardIncomeDetails';

// ── StoreCard ─────────────────────────────────────────────────────────────────
export function StoreCard({
  client, expanded, onToggle, isDragOver, hasSelected,
  onDragOver, onDragLeave, onDrop, onClick, onUnlink,
  canEdit = false,        // 관리자만 true — 상담자 배분율 편집 버튼 표시 여부
  onEditShareRate,        // (contractId, currentRate, defaultRate) => void
}) {
  const { minus, plus, netTotal } = client;
  const isProfit = netTotal >= 0;

  // 계약 상품 요약: 카드 표면 미리보기용 — 주계약/서비스 분리하여 두 줄로 표시
  // contract_items 배열 기반 (ticketTypeName, quantity, isService)
  const { contractMain, contractSvc } = (() => {
    const main = {}, svc = {};
    for (const c of minus.contracts) {
      for (const item of (c.contractItems || [])) {
        const name = item.ticketTypeName || '?';
        if (item.isService) {
          svc[name] = (svc[name] || 0) + Number(item.quantity);
        } else {
          main[name] = (main[name] || 0) + Number(item.quantity);
        }
      }
    }
    const mainStr = Object.entries(main).map(([k, v]) => `${k}×${v}`).join(' · ');
    const svcStr  = Object.entries(svc).map(([k, v]) => `${k}×${v}`).join(' · ');
    return { contractMain: mainStr, contractSvc: svcStr };
  })();

  // 드래그오버/선택 스타일 오버라이드
  const cardStyle = {
    ...s.storeCard,
    ...(isDragOver  ? { borderColor: C.accent, background: C.accentBg, boxShadow: `0 0 0 3px ${C.accentBdr}` } : {}),
    ...(hasSelected && !isDragOver ? { borderColor: C.accent, cursor: 'pointer' } : {}),
  };

  return (
    <div style={cardStyle} onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}>
      {/* 카드 헤더 */}
      <div
        style={s.cardHead}
        onClick={(e) => { e.stopPropagation(); if (hasSelected) onClick(); else onToggle(); }}
      >
        {/* 상단: 날짜 + 매장명 + chevron */}
        <div style={s.cardHeadTop}>
          <div style={s.cardHeadTopLeft}>
            <div style={s.cardTopRow}>
              {client.latestDate && <span style={s.storeDate}>{fmtDate(client.latestDate)}</span>}
              {/* 계약취소 배지 — 접힌 상태에서도 표시 */}
              {minus.contracts.some(c => c.isCancelled) && (
                <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.12)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', flexShrink: 0, whiteSpace: 'nowrap' }}>
                  계약취소
                </span>
              )}
              {(isDragOver || hasSelected) && (
                <span style={s.connectHint}>{isDragOver ? '놓으면 연결' : '탭하여 연결'}</span>
              )}
            </div>
            <div style={s.storeName}>{client.name}</div>
          </div>
          <div style={{ ...s.toggleChevron, transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)' }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={C.sub} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* 계약 요약 (있을 때) */}
        {contractMain && <div style={s.contractSummary}>{contractMain}</div>}
        {contractSvc && (
          <div style={s.contractSvcRow}>
            <span style={s.svcBadge}>서비스</span>
            <span style={s.contractSvcText}>{contractSvc}</span>
          </div>
        )}

        {/* 금액 바: 차감 / 수입 / 순수익 — 3등분 풀너비 */}
        <div style={s.cardAmtBar}>
          <div style={s.cardAmtItem}>
            <span style={s.cardAmtLabel}>차감</span>
            <span style={{ ...s.cardAmtVal, color: C.minus }}>−{fmt(minus.minusTotal)}</span>
          </div>
          <div style={s.cardAmtDivider} />
          <div style={s.cardAmtItem}>
            <span style={s.cardAmtLabel}>수입</span>
            <span style={{ ...s.cardAmtVal, color: plus.plusTotal >= 0 ? C.plus : C.minus }}>{plus.plusTotal >= 0 ? '+' : ''}{fmt(plus.plusTotal)}</span>
          </div>
          <div style={s.cardAmtDivider} />
          <div style={s.cardAmtItem}>
            <span style={s.cardAmtLabel}>순수익</span>
            <span style={{ ...s.cardAmtVal, color: isProfit ? C.plus : C.minus, fontWeight: 800 }}>
              {netTotal >= 0 ? '+' : ''}{fmt(netTotal)}
            </span>
          </div>
        </div>
      </div>

      {/* 카드 상세 (펼침) */}
      {expanded && (
        <div style={s.cardBody} onClick={(e) => e.stopPropagation()}>

          {/* 계약 차감 */}
          {minus.contracts.length > 0 && (
            <DetailSection label="계약 차감" total={minus.contractsTotal} color={C.minus} sign="−">
              {minus.contracts.map((c) => {
                // contract_items 배열에서 주계약·서비스 분리
                const normalItems  = (c.contractItems || []).filter(i => !i.isService);
                const serviceItems = (c.contractItems || []).filter(i => i.isService);
                const label = normalItems.map(i => `${i.ticketTypeName}×${i.quantity}`).join(' · ') || '—';
                const svcLabel = serviceItems.map(i => `${i.ticketTypeName}×${i.quantity}`).join(' · ');
                return (
                  <div key={c.id} style={s.dRow}>
                    <div style={s.dLine}>
                      <span style={s.dDate}>{fmtDate(c.contractDate)}</span>
                      {c.isCancelled ? (
                        /* 취소 건: 계약취소 배지만 표시, 차감 없음 */
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.12)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)' }}>
                          계약취소
                        </span>
                      ) : (
                        <>
                          <span style={s.dLabel}>{label}</span>
                          <span style={{ ...s.dAmt, color: C.minus }}>−{fmt(c.fee)}</span>
                        </>
                      )}
                    </div>
                    {c.isCancelled && (
                      <div style={{ ...s.dLine, paddingLeft: 34, marginTop: 3 }}>
                        <span style={s.dLabel}>{label}</span>
                        <span style={{ ...s.dAmt, color: C.minus }}>0</span>
                      </div>
                    )}
                    {svcLabel && (
                      <div style={s.dSub2}>
                        <span style={s.svcChip}>서비스 {svcLabel}</span>
                      </div>
                    )}
                    {/* 상담자 배분 차감 — 해당 계약 바로 아래 표시 */}
                    {!c.isCancelled && c.consultantShare?.shareAmount != null && c.consultantShare.shareAmount > 0 && (
                      <div style={{ paddingLeft: 12, marginTop: 3 }}>
                        {/* 1행: 라벨 + 금액 + 편집 버튼 */}
                        <div style={{ ...s.dLine }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 5, flex: 1, minWidth: 0 }}>
                            <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.1)', color: C.minus, border: '1px solid rgba(220,38,38,0.2)', flexShrink: 0 }}>
                              상담배분
                            </span>
                            <span style={{ ...s.dLabel, color: C.minus, fontSize: 11 }}>
                              {c.consultantShare.consultantName}
                            </span>
                            {/* 오버라이드 표시 */}
                            {c.consultantShare.isOverridden && (
                              <span style={{ fontSize: 9, padding: '0px 4px', borderRadius: 3, background: 'rgba(234,179,8,0.15)', color: '#b45309', border: '1px solid rgba(234,179,8,0.3)', flexShrink: 0 }}>수정됨</span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ ...s.dAmt, color: C.minus }}>−{fmt(Math.abs(c.consultantShare.shareAmount))}</span>
                            {/* 관리자만 편집 버튼 표시 */}
                            {canEdit && (
                              <button
                                onClick={(e) => { e.stopPropagation(); onEditShareRate?.(c.id, c.consultantShare.shareRate, c.consultantShare.defaultRate); }}
                                style={{ fontSize: 10, padding: '1px 7px', borderRadius: 4, border: '1px solid var(--border)', background: 'var(--bg-card)', color: 'var(--text-sub)', cursor: 'pointer', flexShrink: 0 }}
                              >
                                편집
                              </button>
                            )}
                          </div>
                        </div>
                        {/* 2행: 계약자% / 배분자% — 누가 얼마씩 가져가는지 명확히 표기 */}
                        <div style={{ display: 'flex', gap: 8, marginTop: 2, paddingLeft: 2 }}>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            계약자 <strong style={{ color: 'var(--text-sub)' }}>{100 - c.consultantShare.shareRate}%</strong>
                          </span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>·</span>
                          <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>
                            {c.consultantShare.marketerName} <strong style={{ color: C.minus }}>{c.consultantShare.shareRate}%</strong>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </DetailSection>
          )}

          {/* 택배비 차감 */}
          {minus.delivery && minus.delivery.length > 0 && (
            <DetailSection label="택배비 차감" total={minus.deliveryTotal} color={C.minus} sign="−">
              {minus.delivery.map((d, i) => (
                <div key={i} style={s.dRow}>
                  <div style={s.dLine}>
                    {/* 왼쪽 묶음: 택배 텍스트 + 회수 칩 — flex:1 래퍼로 왼쪽에 고정 */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, minWidth: 0 }}>
                      <span style={{ color: C.main, fontWeight: 500, fontSize: 12, flexShrink: 0 }}>택배</span>
                      {/* 회수 칩 — is_return=true일 때 표시 (택배비 ×2 적용 중) */}
                      {d.isReturn && (
                        <span style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 4, background: 'rgba(220,38,38,0.12)', color: '#dc2626', border: '1px solid rgba(220,38,38,0.3)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          회수 ✓
                        </span>
                      )}
                    </div>
                    <span style={{ ...s.dAmt, color: C.minus }}>−{fmt(d.fee)}</span>
                  </div>
                  {/* 모바일: 택배사+송장번호 텍스트 대신 복사 버튼만 표시 */}
                  {d.carrier && d.trackingNo && (
                    <div style={s.dTrackRow}>
                      <CopyTrackingButton text={`${d.carrier} ${d.trackingNo}`} />
                    </div>
                  )}
                </div>
              ))}
            </DetailSection>
          )}

          {/* 홍보물 차감 */}
          {minus.promotional.length > 0 && (
            <DetailSection label="홍보물 차감" total={minus.promoTotal} color={C.minus} sign="−">
              {minus.promotional.map((po) =>
                po.items.map((i) => (
                  <div key={i.id} style={s.dRow}>
                    <div style={s.dLine}>
                      <span style={s.dDate}>{fmtDate(po.receivedDate)}</span>
                      <span style={s.dLabel}>
                        {i.item_name}<span style={s.dQty}> {i.quantity}개</span>
                      </span>
                      <span style={{ ...s.dAmt, color: C.minus }}>−{fmt(Number(i.quantity) * Number(i.unit_price))}</span>
                    </div>
                    {/* 모바일: 택배사+송장번호 텍스트 대신 복사 버튼만 표시 */}
                    {i.carrier && i.tracking_no && (
                      <div style={s.dTrackRow}>
                        <CopyTrackingButton text={`${i.carrier} ${i.tracking_no}`} />
                      </div>
                    )}
                  </div>
                ))
              )}
            </DetailSection>
          )}

          {/* 홍보물 예정 — 아직 정산 차감에는 넣지 않음 */}
          {(minus.promotionalPending || []).length > 0 && (
            <DetailSection label="홍보물 예정" total={minus.promoPendingTotal || 0} color="#b45309" sign="">
              {minus.promotionalPending.map((po) =>
                po.items.map((i) => (
                  <div key={i.id} style={s.dRow}>
                    <div style={s.dLine}>
                      <span style={s.dDate}>{fmtDate(po.receivedDate)}</span>
                      <span style={s.dLabel}>
                        {i.item_name}<span style={s.dQty}> {i.quantity}개</span>
                      </span>
                      <span style={{ ...s.dAmt, color: '#b45309' }}>{fmt(Number(i.quantity) * Number(i.unit_price))}</span>
                    </div>
                    <div style={s.dTrackRow}>
                      <span style={{ color: '#b45309', fontSize: 10, fontWeight: 700 }}>정산 미반영</span>
                    </div>
                  </div>
                ))
              )}
            </DetailSection>
          )}

          {/* 수입 섹션 (카드승인·카드업로드·전화승인·입금) */}
          <StoreCardIncomeDetails plus={plus} onUnlink={onUnlink} />

        </div>
      )}
    </div>
  );
}

// DetailSection, UnlinkBtn은 StoreCardDetail.jsx로 분리됨 — re-export for backward compat
export { DetailSection, UnlinkBtn } from './StoreCardDetail';
